import React from 'react';
import { InventoryByLocation } from '../types';

interface LocationInventoryProps {
  inventory: InventoryByLocation[];
  title?: string;
}

export function LocationInventory({ inventory, title = 'Stock at Each Location' }: LocationInventoryProps) {
  if (!inventory.length) {
    return (
      <div style={styles.empty}>
        <span style={styles.emptyIcon}>&#x26A0;</span>
        No location inventory data available.
      </div>
    );
  }

  const total = inventory.reduce((sum, loc) => sum + loc.available, 0);

  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <h3 style={styles.heading}>{title}</h3>
        <span style={styles.totalBadge}>
          {total} total in stock
        </span>
      </div>
      <div style={styles.list}>
        {inventory.map((loc, i) => {
          const inStock = loc.available > 0;
          return (
            <div key={i} style={styles.row}>
              <div style={styles.locationInfo}>
                <span style={styles.locationName}>{loc.locationName}</span>
                {loc.city && <span style={styles.city}>{loc.city}</span>}
              </div>
              <div style={styles.stockRight}>
                <span
                  style={{
                    ...styles.stockDot,
                    background: inStock ? '#48bb78' : '#fc8181',
                  }}
                />
                <span
                  style={{
                    ...styles.stockCount,
                    color: inStock ? '#48bb78' : '#fc8181',
                  }}
                >
                  {loc.available}
                </span>
                <span style={styles.stockLabel}>
                  {inStock ? 'in stock' : 'out of stock'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    background: '#1a1a1a',
    borderRadius: '12px',
    padding: '20px 24px',
    border: '1px solid #2d3748',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  heading: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 700,
    color: '#a0aec0',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  totalBadge: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#4a5568',
    background: '#2d3748',
    borderRadius: '20px',
    padding: '4px 12px',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid #2d3748',
  },
  locationInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  locationName: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#e2e8f0',
  },
  city: {
    fontSize: '13px',
    color: '#718096',
  },
  stockRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  stockDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  stockCount: {
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: 1,
  },
  stockLabel: {
    fontSize: '13px',
    color: '#718096',
    fontWeight: 500,
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#718096',
    fontSize: '16px',
    padding: '16px',
  },
  emptyIcon: {
    fontSize: '20px',
  },
};
